import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import { LabelTemplateSchema, type LabelTemplate } from '@etiquetachef/types';
import type { DocResult } from '@/lib/firestoreTypes';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestoreLabelTemplates';

/** Estado da aba Modelos do detalhe do tenant. */
export type LabelTemplateListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; templates: LabelTemplate[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lista templates do tenant ordenados por `updatedAt desc`. Single-field
 * index — não precisa de composto em `firestore.indexes.json`. Docs que
 * falham no parse Zod ficam logados e ignorados (defesa em profundidade
 * contra docs antigos durante migrações de schema).
 *
 * Origem: sem issue — commit 764223f16, 2026-05-05 (listener + CRUD de labelTemplates).
 *
 * Usado por: `features/labelTemplates/useLabelTemplates.ts`.
 *
 * Arquitetura: rule `match /labelTemplates/{templateId}` sob `match /tenants/{tenantId}`.
 *
 * Dados: `tenants/{tid}/labelTemplates/{id}` — `LabelTemplateSchema`.
 *
 * Falhas conhecidas:
 *  - Modelo fora do schema some só com `console.warn`, sem report (#926).
 */
export function subscribeLabelTemplates(
  tenantId: string,
  onResult: (r: LabelTemplateListResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  const ref = collection(firestore, 'tenants', tenantId, 'labelTemplates');
  const q = query(ref, orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      const templates: LabelTemplate[] = [];
      for (const d of snap.docs) {
        // B-24: normaliza Timestamp→millis antes do parse Zod (campos
        // `updatedAt`/`createdAt` gravados via `serverTimestamp()` chegam
        // como `Timestamp` no SDK web; o schema espera `z.number()`).
        const parsed = LabelTemplateSchema.safeParse({ id: d.id, ...snapshotData(d) });
        if (parsed.success) {
          templates.push(parsed.data);
        } else {
          console.warn(`${TAG}: template ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', templates });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/** `DocResult` de um modelo. */
export type LabelTemplateDocResult = DocResult<LabelTemplate>;

/**
 * Assina um modelo de etiqueta pelo id (`NOT_FOUND` se não existe).
 *
 * Origem: sem issue — commit 764223f16, 2026-05-05.
 *
 * Usado por: nenhum consumidor hoje (`rg 'subscribeLabelTemplate\b'` em
 * 2026-10-10) — export sem uso, registrado como achado na #926.
 */
export function subscribeLabelTemplate(
  tenantId: string,
  templateId: string,
  onResult: (r: LabelTemplateDocResult) => void,
): Unsubscribe {
  const ref = doc(firestore, 'tenants', tenantId, 'labelTemplates', templateId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onResult({ kind: 'NOT_FOUND' });
        return;
      }
      // B-24: normaliza Timestamp→millis antes do parse (idem subscribeLabelTemplates).
      const parsed = LabelTemplateSchema.safeParse({ id: snap.id, ...snapshotData(snap) });
      if (!parsed.success) {
        onResult({
          kind: 'ERROR',
          error: new Error(`zod parse failed for ${snap.ref.path}`),
        });
        return;
      }
      onResult({ kind: 'READY', data: parsed.data });
    },
    (err) => {
      console.error(`${TAG}: doc snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
